import { useAtomValue } from 'jotai'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Box from '@mui/joy/Box'
import Skeleton from '@mui/joy/Skeleton'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import CheckoutButton from './checkout-button'
import { WEB_ROUTE_SEGMENTS } from '../constants'
import type {
    Currency,
    Garage,
    HoverPoint,
    LaterAvailabilityTime,
    SelectedPoint,
} from '../types'
import { getReservationBasePath } from '../helpers'
import { ROUTE_SEGMENTS as PWA_ROUTE_SEGMENTS } from '../pwa/constants'
import { searchSubmissionAtom } from '../state'
import { useApplySuggestedStartTime } from '../hooks/use-apply-suggested-start-time'
import { getGarageParkingEndMessage, getGarageParkingPeriod } from '../garage-parking-period'

export default function GarageCard({
    garage,
    loadingGarageAvailability,
    onMap,
    hoverPoint,
    setHoverPoint,
    setSelectedId,
    availableLaterAt,
    currency,
    navigateToGarage,
} : {
    garage: Garage,
    loadingGarageAvailability: boolean,
    onMap?: boolean,
    hoverPoint?: HoverPoint | null,
    setHoverPoint?: ((arg1: HoverPoint | null) => void) | null,
    setSelectedId?: ((arg1: SelectedPoint | null) => void) | null,
    availableLaterAt?: LaterAvailabilityTime | null,
    currency: Currency,
    navigateToGarage?: ((arg1: number) => void) | null | undefined,
}) {

    const searchSubmission = useAtomValue(searchSubmissionAtom)
    const applySuggestedStartTime = useApplySuggestedStartTime({ urlVariant: 'map' })

    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const userLang = i18n.language
    const isPwaRoute = location.pathname === '/pwa' || location.pathname.startsWith('/pwa/')

    const parkingPeriod = getGarageParkingPeriod(garage.reservationTimeRange)
    const amount = currency === 'eur' ? garage.amountCentsEur : garage.amountCentsCzk
    const hourlyAmount = currency === 'eur' ? garage.amountCentsHourlyEur : garage.amountCentsHourlyCzk
    const amountLoaded = amount != null && Number.isFinite(amount) && parkingPeriod !== null
    const amountHourlyLoaded = amountLoaded && hourlyAmount != null && Number.isFinite(hourlyAmount)
    const minimumStayDuration = amountLoaded && garage.reservationTimeRange?.minimumStayApplied && parkingPeriod
        ? t('minimumStayPrice.duration', { count: parkingPeriod.durationHours })
        : null
    const minimumStayNotice = minimumStayDuration && parkingPeriod
        ? getGarageParkingEndMessage(parkingPeriod, t)
        : null

    const garagePriceCzk = amountLoaded ? garage.amountCentsCzk! / 100 : '---'
    const garagePriceEur = amountLoaded ? garage.amountCentsEur! / 100 : '---'
    const garagePriceHourlyCzk = amountHourlyLoaded ? garage.amountCentsHourlyCzk! / 100 : '--'
    const garagePriceHourlyEur = amountHourlyLoaded ? garage.amountCentsHourlyEur! / 100 : '--'
    const garagePrice = currency === 'eur' ?  `~€${garagePriceEur}` : `${garagePriceCzk} Kč`
    const garagePriceHourly = currency === 'eur' ? 
        `~€${garagePriceHourlyEur}/${t('general.hoursShortcut')}` :
        `${garagePriceHourlyCzk} Kč/${t('general.hoursShortcut')}`

    const reservationBasePath = getReservationBasePath(
        userLang,
        t('routes.reservations'),
        t('routes.reservations_steps.reservation_time'),
        garage.reservationSlug,
    )

    const reservationLink = `${reservationBasePath}?reservation_step_reservation_time%5Bbegins_at_date%5D=${
        searchSubmission.startDate?.format('YYYY-MM-DD') ?? ''}&reservation_step_reservation_time%5Bbegins_at_time%5D=${
        searchSubmission.startTime ?? ''}&reservation_step_reservation_time%5Bends_at_date%5D=${
        searchSubmission.endDate?.format('YYYY-MM-DD') ?? ''}&reservation_step_reservation_time%5Bends_at_time%5D=${
        searchSubmission.endTime ?? ''}&reservation_step_reservation_time%5Bgarage_id%5D=${
        garage.id}&reservation_step_reservation_time%5Blocation%5D=${
        searchSubmission.location ?? ''}`
    const checkoutPath = isPwaRoute
        ? `/pwa/${PWA_ROUTE_SEGMENTS.checkout}`
        : `/${userLang}/${WEB_ROUTE_SEGMENTS.checkout[userLang as keyof typeof WEB_ROUTE_SEGMENTS.checkout]}`

    const checkoutDisabled = loadingGarageAvailability || garage.timeRangeValid !== true || !garage.available || !amountLoaded
    const canApplyLaterAvailability =
        !loadingGarageAvailability &&
        garage.timeRangeValid === true &&
        !garage.available &&
        Boolean(availableLaterAt)
    const checkoutLabel =
        !loadingGarageAvailability && garage.timeRangeValid !== true ? t('general.unavailable') :
            !garage.available && !loadingGarageAvailability ? t('general.occupied') : t('general.bookNow')
    
    return (
        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                bgcolor: 'white',
                borderRadius: '5px',
                border: '1px solid var(--joy-palette-neutral-200)',
                overflow: 'hidden',
                boxShadow: `var(--joy-shadowRing, 0 0 #000),
                    0.3px 0.8px 1.1px rgba(187 187 187 / 0.11),
                    0.5px 1.3px 1.8px -0.6px rgba(187 187 187 / 0.18),
                    1.1px 2.7px 3.8px -1.2px rgba(187 187 187 / 0.26)`,
                ...onMap ? {
                    border: 'none',
                    boxShadow: 'none',
                    minWidth: '240px',
                    maxWidth: '460px',
                    flexDirection: 'column',
                } : {},
                ...(!onMap && hoverPoint?.id === garage.id) ? {
                    border: '1px solid black',
                } : {},
                fontFamily: 'var(--joy-fontFamily-body)',
                cursor: 'auto',
                color: 'black',
            }}
            onMouseEnter={!onMap && setHoverPoint ? () =>setHoverPoint({
                id: garage.id,
                lat: garage.lat,
                lng: garage.lng,
            }) : () => {}}
            onMouseLeave={!onMap && setHoverPoint ? () =>setHoverPoint(null) : () => {}}
            onClick={!onMap && setSelectedId ? () =>setSelectedId({
                id: garage.id,
                lat: garage.lat,
                lng: garage.lng,
                fromList: !onMap,
            }) :
                (onMap && navigateToGarage) ?
                    () => navigateToGarage(garage.id) :
            () => {}}
        >
            {onMap &&
                <Box
                    onClick={setSelectedId ? e => {
                        e.stopPropagation()
                        setSelectedId(null)
                        if(setHoverPoint) {
                            setHoverPoint(null)
                        }
                        
                    } : () => {}}
                    sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        backgroundColor: 'white',
                        borderRadius: '0.25rem',
                        opacity: 0.5,
                        zIndex: 1,
                        "&:hover": {
                            opacity: 0.7,
                            cursor: 'pointer',
                        },
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="black"  xmlns="http://www.w3.org/2000/svg">
                        <title>Close icon</title>
                        <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                    </svg>
                </Box>
            }
            {garage.lastPlacesAvailable &&
                <Box sx={{
                    position: 'absolute',
                    top: '0.75rem',
                    left: 0,
                    backgroundColor: 'var(--joy-palette-secondary-400)',
                    padding: '0.2rem 0.3rem',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    borderTopRightRadius: '0.25rem',
                    borderBottomRightRadius: '0.25rem',
                    zIndex: 1,
                }}>
                    {t('general.lastPlacesAvailable')}
                </Box>
            }
            {onMap &&
                <Box
                    component={(navigateToGarage || !garage.available) ? "div" : "a"}
                    href={(navigateToGarage || !garage.available) ? undefined : reservationLink}
                    style={{
                        color: 'inherit',
                        textDecoration: 'none',
                    }}
                >
                    <img
                        src={garage.image}
                        loading="lazy"
                        alt=""
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                        }}
                    />
                </Box>
            }
            {!onMap &&
                <img
                    src={garage.image}
                    loading="lazy"
                    alt=""
                    style={{
                        flex: '0 0 35%',
                        maxWidth: '35%',
                        objectFit: 'cover',
                    }}
                />
            }
            <Box
                sx={{
                    padding: {
                        xs: '0.65rem',
                        lg: '0.75rem',
                    },
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box display="flex" justifyContent="space-between">
                    <Typography sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: {
                            xs: onMap ? '180px' : 'calc(65vw - 7rem)',
                            md: '194px',
                        },
                        paddingRight: onMap ? '1rem' : '0.5rem',
                        fontWeight: onMap ? 500 : 600,
                        fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                        },
                    }}>
                        {onMap && garage.available &&
                            <Box
                                component={navigateToGarage ? "span" : "a"}
                                href={navigateToGarage ? undefined : reservationLink}
                                style={{
                                    color: 'black',
                                    textDecoration: 'none',
                                }}
                            >
                                {garage.name}
                            </Box>
                        }
                        {(!onMap || !garage.available) && garage.name}
                    </Typography>
                    <Typography sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: onMap ? 500 : 600,
                        fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                        },
                    }}>
                        {garage.priceLoading ? <Skeleton>{garagePrice}</Skeleton> : garagePrice}
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} mb={0.5}>
                    <Box sx={{
                        'svg' : {
                            width: '1rem',
                            height: '1rem',
                            fill: '#BDBDBD',
                        },
                        display: 'flex',
                        flexShrink: 0,
                        alignItems: 'center',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="black"  xmlns="http://www.w3.org/2000/svg">
                            <title>Car icon</title>
                            <path fillRule="evenodd" clipRule="evenodd" d="M18.92 5.01C18.72 4.42 18.16 4 17.5 4H6.5C5.84 4 5.29 4.42 5.08 5.01L3 11V19C3 19.55 3.45 20 4 20H5C5.55 20 6 19.55 6 19V18H18V19C18 19.55 18.45 20 19 20H20C20.55 20 21 19.55 21 19V11L18.92 5.01ZM6.85 6H17.14L18.22 9.11H5.77L6.85 6ZM5 16H19V11H5V16ZM7.5 12C6.67157 12 6 12.6716 6 13.5C6 14.3284 6.67157 15 7.5 15C8.32843 15 9 14.3284 9 13.5C9 12.6716 8.32843 12 7.5 12ZM15 13.5C15 12.6716 15.6716 12 16.5 12C17.3284 12 18 12.6716 18 13.5C18 14.3284 17.3284 15 16.5 15C15.6716 15 15 14.3284 15 13.5Z" />
                        </svg>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="black"  xmlns="http://www.w3.org/2000/svg">
                            <title>Height icon</title>
                            <path fillRule="evenodd" clipRule="evenodd" d="M4 5V3H20V5H4ZM11 11H8L12 7L16 11H13V21H11V11Z" />
                        </svg>
                        <Typography level="body-sm" ml={0.5} sx={{
                            fontSize: {
                                xs: '0.75rem',
                                sm: '0.875rem',
                            },
                        }}>{garage.height}m</Typography>
                    </Box>
                    <Typography level="body-sm" sx={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                        },
                    }}>
                        {garage.priceLoading ? <Skeleton>{garagePriceHourly}</Skeleton> : minimumStayDuration ?? garagePriceHourly}
                    </Typography>
                </Box>
                {minimumStayNotice &&
                    <Typography level="body-sm" sx={{
                        display: 'block',
                        color: 'customGreen.dark',
                        textAlign: 'right',
                        mb: 0.5,
                        fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                        },
                    }}>
                        <AccessTimeRoundedIcon sx={{ color: 'inherit', fontSize: '1.15em', verticalAlign: '-0.2em', mr: 0.5 }} />
                        <Box component="span">{minimumStayNotice}</Box>
                    </Typography>
                }
                <Box flexGrow={1} />
                <Box display="flex" justifyContent="flex-end" gap={0.5} mt={0.5}>
                    <Button
                        component={navigateToGarage ? "div" : "a"}
                        href={navigateToGarage ? undefined : reservationLink}
                        size='sm'
                        variant='plain'
                        sx={{
                            border: '1px solid var(--joy-palette-primary-300)',
                            transition: '250ms background-color',
                            "&:hover": {
                                backgroundColor: 'var(--joy-palette-primary-100)',
                            },
                            ...onMap ? {
                                fontWeight: 500,
                            } : {},
                        }}
                        onClick={e => {
                            e.stopPropagation()
                            navigateToGarage ? navigateToGarage(garage.id) : null
                        }}
                    >
                        {t('general.detail')}
                    </Button>
                    {canApplyLaterAvailability && availableLaterAt ?
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={event => {
                                event.stopPropagation()
                                applySuggestedStartTime(availableLaterAt)
                            }}
                            sx={{
                                backgroundColor: '#FFC400',
                                color: '#000000',
                                borderRadius: '0.25rem',
                                '&:hover': {
                                    backgroundColor: '#000000',
                                    color: '#FFC400',
                                },
                                ...onMap ? {
                                    fontWeight: 500,
                                } : {},
                            }}
                        >
                            {t('general.moveToTime', { time: availableLaterAt })}
                        </Button>
                    :
                        <CheckoutButton
                            garageId={garage.id}
                            showPopup={garage.showPopup}
                            popupText={garage.popupText}
                            onSuccess={() => navigate(checkoutPath)}
                            label={checkoutLabel}
                            size='sm'
                            loading={loadingGarageAvailability || garage.priceLoading}
                            disabled={checkoutDisabled}
                            stopPropagation={true}
                            sx={{
                                backgroundColor: '#FFC400',
                                color: '#000000',
                                borderRadius: '0.25rem',
                                "&:hover": {
                                    backgroundColor: '#000000',
                                    color: '#FFC400',
                                },
                                ...loadingGarageAvailability ? {
                                    pointerEvents: 'none',
                                } : {},
                                ...onMap ? {
                                    fontWeight: 500,
                                } : {},
                                ...checkoutDisabled ? {
                                    '&:disabled': {
                                        cursor: 'default',
                                        color: '#fff',
                                        backgroundColor: '#828282',
                                        opacity: 'unset',
                                    },
                                } : {},
                            }}
                        />
                    }
                </Box>
            </Box>
        </Box>
    )
}
